import React, { useState, useEffect } from 'react';
import { TrendingDown, AlertCircle } from 'lucide-react';

interface PointFeedbackProps {
  type: 'deduction' | 'insufficient';
  amount?: number;
  position?: { x: number; y: number };
  lang?: string;
  onComplete?: () => void;
}

export const PointFeedback: React.FC<PointFeedbackProps> = ({ type, amount, position, lang = 'ko', onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const duration = position ? 2000 : 2500;
    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) onComplete();
    }, duration);

    return () => clearTimeout(timer);
  }, [onComplete, position]);

  if (!isVisible) return null;

  // 툴팁 위치 및 스타일 계산 (위로 더 올림)
  const tooltipStyle: React.CSSProperties = position 
    ? { 
        position: 'fixed', 
        left: position.x, 
        top: position.y - 12, // 기존보다 더 위로 올림
        transform: 'translate(-50%, -100%)',
        zIndex: 1000,
        pointerEvents: 'none'
      }
    : {
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none'
      };

  const texts = {
    deduction: lang === 'ko' ? '포인트 차감' : 'Deducted',
    insufficient: lang === 'ko' ? '포인트 부족' : 'Insufficient',
    recharge: lang === 'ko' ? '충전이 필요합니다' : 'Recharge Required'
  };

  if (position) {
    return (
      <div style={tooltipStyle} className="animate-bounce-short">
        <style>
          {`
            @keyframes bounce-short {
              0%, 100% { transform: translate(-50%, -100%); }
              50% { transform: translate(-50%, -115%); }
            }
            .animate-bounce-short {
              animation: bounce-short 1s ease-in-out infinite;
            }
          `}
        </style>
        <div className={`relative px-4 py-2.5 rounded-2xl shadow-2xl flex items-center gap-2 border-2 animate-in slide-in-from-bottom-2 fade-in duration-300 ${
          type === 'deduction' 
            ? 'bg-slate-900 border-white/20 text-white' 
            : 'bg-rose-600 border-rose-400 text-white'
        }`}>
          {/* 말풍선 꼬리 */}
          <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[8px] w-4 h-4 rotate-45 border-r-2 border-b-2 ${
            type === 'deduction' ? 'bg-slate-900 border-white/20' : 'bg-rose-600 border-rose-400'
          }`} />
          
          <div className={`flex items-center gap-2`}>
            <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${type === 'deduction' ? 'bg-white/10' : 'bg-white/20'}`}>
              {type === 'deduction' ? (
                <TrendingDown size={14} className="text-rose-400" />
              ) : (
                <AlertCircle size={14} className="text-white" />
              )}
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[9px] font-black uppercase tracking-widest opacity-60">
                {type === 'deduction' ? texts.deduction : texts.insufficient}
              </span>
              <span className="text-sm font-black tracking-tight">
                {type === 'deduction' ? `-${amount} P` : texts.recharge}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 기존 중앙 모달 방식 (fallback)
  return (
    <div style={tooltipStyle}>
      <div className={`flex flex-col items-center gap-4 p-8 rounded-[40px] backdrop-blur-xl border shadow-2xl animate-in zoom-in-95 fade-in duration-300 ${
        type === 'deduction' ? 'bg-slate-900/90 border-white/20 text-white' : 'bg-rose-500/90 border-rose-400/20 text-white'
      }`}>
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${type === 'deduction' ? 'bg-white/10' : 'bg-white/20'}`}>
          {type === 'deduction' ? (
            <TrendingDown size={32} className="text-rose-400" />
          ) : (
            <AlertCircle size={32} className="text-white" />
          )}
        </div>
        <div className="text-center">
          <h3 className="text-xl font-black uppercase tracking-tight mb-1">
            {type === 'deduction' ? 'Points Deducted' : 'Insufficient Points'}
          </h3>
          <p className="text-sm font-bold uppercase tracking-widest opacity-80">
            {type === 'deduction' ? `-${amount} P` : 'Recharge Required'}
          </p>
        </div>
      </div>
    </div>
  );
};
